import { AutocompleteRenderGetTagProps, Chip, chipClasses, ChipProps, styled } from '@mui/material'
import { UserAvatar } from './UserAvatar'
import { UserAvatarGroup } from './UserAvatarGroup'
import { notProp, orUndef } from '@portal/pkg-utils'
import { DisplayableUser } from './model'
import { useUserDisplayName } from './useUserDisplayName'

export const NAME = 'MultiUserTags'

export interface MultiUserTagsProps {
  users: ReadonlyArray<DisplayableUser>
  getTagProps: AutocompleteRenderGetTagProps
  size?: 'small'
  maxUsers?: number
  compact?: boolean
  readOnly?: boolean
}

const StyledChip = styled(Chip, {
  name: NAME,
  slot: 'Chip',
  shouldForwardProp: notProp('size'),
})<ChipProps & { size: MultiUserTagsProps['size'] }>(({ size }) => ({
  [`.${chipClasses.avatar}`]: {
    color: '#fff',
    ...(size &&
      size === 'small' && {
        height: 16,
        width: 16,
        fontSize: '0.55rem',
        marginLeft: 1,
      }),
  },
  [`.${chipClasses.deleteIcon}`]: {
    ...(size && size === 'small' && { fontSize: 14 }),
  },
  ...(size && size === 'small' && { height: 20 }),
}))

export const MultiUserTags: React.FC<MultiUserTagsProps> = ({
  users,
  getTagProps,
  size,
  maxUsers,
  compact,
  readOnly,
}) => {
  const displayName = useUserDisplayName()

  if (compact) {
    return (
      <UserAvatarGroup
        users={users}
        variant={orUndef(readOnly && 'compressed')}
        maxUsers={maxUsers}
        size="small"
        sx={{ mb: 0.5 }}
      />
    )
  }

  return (
    <>
      {users.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index })
        return (
          <StyledChip
            key={key}
            size={size}
            variant="outlined"
            avatar={<UserAvatar {...option} sx={{ width: 16, height: 16, fontSize: '0.55rem' }} />}
            label={displayName(option)}
            data-testid={`${NAME}-Chip-${option.identifier}`}
            {...tagProps}
          />
        )
      })}
    </>
  )
}
